import { createAsyncThunk } from "@reduxjs/toolkit";

import * as basketAPI from "../../../api/basket";
// Import the concrete module, not the `apps/checkout/thunks` barrel —
// that barrel re-exports this file, so it would create a cycle.
import { loadBasket } from "../../../apps/checkout/thunks/loadBasket";
import { IBasket } from "../../../models/catalogue";
import { IBasketLineAPIURL } from "../../../models/nominals";

/**
 * Arguments for removeBasketLine thunk.
 */
interface RemoveBasketLineArgs {
    lineURL: IBasketLineAPIURL;
}

/**
 * Remove a line from the basket.
 *
 * This thunk removes a product line from the user's basket and reloads
 * the basket to reflect updated totals and offers. Used in cart views
 * and checkout flows when removing items.
 *
 * @param {IBasketLineAPIURL} args.lineURL - API URL of the basket line to remove
 * @returns {Promise<IBasket>} Promise resolving to the reloaded basket
 *
 * @example
 * // Remove a line from basket
 * dispatch(removeBasketLine({ lineURL: line.url }));
 *
 * @sideEffects
 * - Makes API call to remove line via basketAPI.removeLine
 * - Dispatches loadBasket thunk to reload basket data
 * - Updates `data.basket` with reloaded basket via extraReducers
 * - May trigger offer recalculations on the backend
 */
// Prefix kept as "checkout/": module only moved, and nothing matches these
// types by string, so renaming would change them unnoticed.
export const removeBasketLine = createAsyncThunk(
    "checkout/removeBasketLine",
    async (args: RemoveBasketLineArgs, { dispatch }): Promise<IBasket> => {
        await basketAPI.removeLine(args.lineURL);
        const result = await dispatch(loadBasket()).unwrap();
        return result.basket;
    },
);
